import {
  DEFAULT_BASE_URL,
  DEFAULT_API_KEY,
  DEFAULT_MODEL,
  DEFAULT_TELEMETRY_ENDPOINT,
} from "./config";
import { OrgPolicy, applyPolicy } from "./policy";

export interface AddinSettings {
  baseUrl: string;
  apiKey: string;
  model: string;
  /**
   * Entra (Azure AD) application (client) ID used for NAA/Graph mailbox access.
   * Empty = fall back to EWS (on-premises Exchange) with limited access.
   * Not a secret — it's a public identifier — so storing it in roamingSettings is fine.
   */
  graphClientId: string;
  /** When true, the current email is auto-classified on open (sends body to the
   * AI endpoint). Off by default — egress is opt-in. */
  autoAnalyze: boolean;
  /** Global switch for the Purview sensitivity-label / content-heuristic gate
   * (see data-classification.ts). `false` = unchanged legacy behavior — no
   * check at all, exactly as before this feature existed. `true` (default) =
   * every email is checked before being sent to the AI endpoint. Org-lockable
   * via config.json (see policy.ts). */
  purviewFilterEnabled: boolean;
  /** Task pane color theme preference (Axis 12). `"system"` (default) follows
   * `prefers-color-scheme`; `"light"`/`"dark"` force that theme regardless of
   * the OS setting. See `theme.ts` for `bnpLightTheme`/`bnpDarkTheme`. */
  themePreference: "system" | "light" | "dark";
  /** Global switch for the deterministic, client-side pre-send redaction
   * filter (see redaction.ts). `true` (default) = every outgoing message is
   * scanned and likely-sensitive substrings (emails, phone numbers, SSNs,
   * card numbers, secret/token shapes) are replaced with a placeholder before
   * the request reaches the AI endpoint. `false` = unchanged legacy behavior
   * — no redaction at all. Complementary to, and independent from,
   * `purviewFilterEnabled` — that gate can block a whole email outright; this
   * one redacts specific substrings from whatever content is actually sent. */
  redactionEnabled: boolean;
  /** Global switch for OTLP telemetry export (see telemetry.ts, ADR-006). `false`
   * (default) = no data ever leaves the client for observability purposes — a
   * true opt-in, not an opt-out. `true` = agent-loop turns, tool calls, and LLM
   * call spans (structural fields only — no email/AI content) are exported to
   * `telemetryEndpoint`. Org-lockable via config.json (see policy.ts). */
  telemetryEnabled: boolean;
  /** OTLP/HTTP traces endpoint URL telemetry is pushed to. No vendor hardcoded —
   * any OTLP-compatible backend that authenticates via a single `Authorization`
   * header works (e.g. a self-hosted Langfuse instance's OTLP route). Empty =
   * telemetry cannot start even if `telemetryEnabled` is true. */
  telemetryEndpoint: string;
  /** Raw value sent as the `Authorization` header on every export request (e.g.
   * `Basic <base64(publicKey:secretKey)>` for Langfuse, or `Bearer <token>` for
   * another OTLP-compatible backend). Never logged — see debug.ts callers. */
  telemetryToken: string;
}

const DEFAULTS: AddinSettings = {
  baseUrl: DEFAULT_BASE_URL,
  apiKey: DEFAULT_API_KEY,
  model: DEFAULT_MODEL,
  graphClientId: "",
  autoAnalyze: false,
  purviewFilterEnabled: true,
  themePreference: "system",
  redactionEnabled: true,
  telemetryEnabled: false,
  telemetryEndpoint: DEFAULT_TELEMETRY_ENDPOINT,
  telemetryToken: "",
};

const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

/**
 * Returns true when the host is a loopback address. Loopback hosts are treated
 * as a secure context by browsers, so plaintext HTTP is acceptable there.
 */
export function isLoopbackHost(hostname: string): boolean {
  return LOOPBACK_HOSTS.has(hostname.toLowerCase());
}

/** True when a base URL points at a loopback host (secure context, key optional). */
export function isLoopbackBaseUrl(url: string): boolean {
  try {
    return isLoopbackHost(new URL(url).hostname);
  } catch {
    return false;
  }
}

/**
 * Validates an AI provider base URL. HTTPS is always allowed; plaintext HTTP is
 * allowed only for loopback hosts (e.g. a local Ollama at http://localhost:11434/v1).
 */
export function isValidBaseUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol === "https:") {
    return true;
  }
  if (parsed.protocol === "http:") {
    return isLoopbackHost(parsed.hostname);
  }
  return false;
}

const THEME_PREFERENCES: ReadonlyArray<AddinSettings["themePreference"]> = [
  "system",
  "light",
  "dark",
];

/** Narrows an arbitrary stored value to a valid theme preference, falling
 * back to the default for anything unrecognized (e.g. a corrupt or
 * pre-migration `roamingSettings` entry) rather than letting a bad value
 * propagate into theme selection. */
function isThemePreference(value: unknown): value is AddinSettings["themePreference"] {
  return typeof value === "string" && (THEME_PREFERENCES as readonly string[]).includes(value);
}

export function loadSettings(): AddinSettings {
  const rs = Office.context.roamingSettings;
  const storedThemePreference = rs.get("themePreference") as unknown;
  return {
    baseUrl: (rs.get("baseUrl") as string | undefined) ?? DEFAULTS.baseUrl,
    apiKey: (rs.get("apiKey") as string | undefined) ?? DEFAULTS.apiKey,
    model: (rs.get("model") as string | undefined) ?? DEFAULTS.model,
    graphClientId: (rs.get("graphClientId") as string | undefined) ?? DEFAULTS.graphClientId,
    autoAnalyze: (rs.get("autoAnalyze") as boolean | undefined) ?? DEFAULTS.autoAnalyze,
    purviewFilterEnabled:
      (rs.get("purviewFilterEnabled") as boolean | undefined) ?? DEFAULTS.purviewFilterEnabled,
    themePreference: isThemePreference(storedThemePreference)
      ? storedThemePreference
      : DEFAULTS.themePreference,
    redactionEnabled:
      (rs.get("redactionEnabled") as boolean | undefined) ?? DEFAULTS.redactionEnabled,
    telemetryEnabled:
      (rs.get("telemetryEnabled") as boolean | undefined) ?? DEFAULTS.telemetryEnabled,
    telemetryEndpoint:
      (rs.get("telemetryEndpoint") as string | undefined) ?? DEFAULTS.telemetryEndpoint,
    telemetryToken: (rs.get("telemetryToken") as string | undefined) ?? DEFAULTS.telemetryToken,
  };
}

/**
 * Resolves effective settings under an org policy: imposed policy fields win
 * over the user's stored override, which wins over the build-time default.
 * `policy` defaults to `{}` (no org policy configured / not yet loaded).
 */
export function resolveSettings(policy: OrgPolicy = {}): AddinSettings {
  return applyPolicy(loadSettings(), policy);
}

/**
 * Distilled, long-term writing-style profile — persisted in `roamingSettings` as a
 * single JSON string under the `styleProfile` key. Deliberately small (bounded by
 * `MAX_STYLE_PROFILE_BYTES`, well under the 32 KB roamingSettings ceiling): we store
 * a distilled summary, never raw conversation history.
 */
export interface StyleProfile {
  tone: string;
  length: string;
  greeting: string;
  signature: string;
  dosAndDonts: string[];
  language: string;
}

/** Serialized size ceiling for a `StyleProfile`, per the plan's "~2 KB" budget. */
export const MAX_STYLE_PROFILE_BYTES = 2048;

/**
 * Loads the persistent style profile from `roamingSettings`. Returns `null` when
 * absent or when the stored value is not valid JSON — a corrupt entry degrades to
 * "no profile" rather than throwing, since this is best-effort context, not
 * critical state.
 */
export function loadStyleProfile(): StyleProfile | null {
  const raw = Office.context.roamingSettings.get("styleProfile") as string | undefined;
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as StyleProfile;
  } catch {
    return null;
  }
}

/**
 * Persists the style profile to `roamingSettings`. Throws if the serialized
 * profile exceeds `MAX_STYLE_PROFILE_BYTES` — callers (e.g. `distillStyleProfile`)
 * should keep the LLM's output compact; this is the last line of defense against
 * an oversized write eating into the 32 KB roamingSettings budget.
 */
export async function saveStyleProfile(profile: StyleProfile): Promise<void> {
  const json = JSON.stringify(profile);
  // `.length` counts UTF-16 code units, not bytes — multi-byte content (non-ASCII
  // names, emoji, CJK) would understate the real cost against roamingSettings'
  // byte-based ceiling. Measure the actual UTF-8 byte size instead.
  const byteLength = new TextEncoder().encode(json).length;
  if (byteLength > MAX_STYLE_PROFILE_BYTES) {
    throw new Error(
      `Style profile exceeds the ${MAX_STYLE_PROFILE_BYTES}-byte limit (${byteLength} bytes).`
    );
  }
  const rs = Office.context.roamingSettings;
  rs.set("styleProfile", json);
  return new Promise((resolve, reject) => {
    rs.saveAsync((result: Office.AsyncResult<void>) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve();
      } else {
        reject(
          new Error(`Failed to save style profile: ${result.error?.message ?? "unknown error"}`)
        );
      }
    });
  });
}

export async function saveSettings(settings: AddinSettings): Promise<void> {
  const rs = Office.context.roamingSettings;
  rs.set("baseUrl", settings.baseUrl);
  rs.set("apiKey", settings.apiKey);
  rs.set("model", settings.model);
  rs.set("graphClientId", settings.graphClientId);
  rs.set("autoAnalyze", settings.autoAnalyze);
  rs.set("purviewFilterEnabled", settings.purviewFilterEnabled);
  rs.set("themePreference", settings.themePreference);
  rs.set("redactionEnabled", settings.redactionEnabled);
  rs.set("telemetryEnabled", settings.telemetryEnabled);
  rs.set("telemetryEndpoint", settings.telemetryEndpoint);
  rs.set("telemetryToken", settings.telemetryToken);
  return new Promise((resolve, reject) => {
    rs.saveAsync((result: Office.AsyncResult<void>) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve();
      } else {
        reject(new Error(`Failed to save settings: ${result.error?.message ?? "unknown error"}`));
      }
    });
  });
}
